import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useMenuSettings, type MenuVisibility } from './MenuSettingsContext';
import { getSectionTour, SectionTourConfig } from '../config/sectionTours';

export interface TourStep {
  id: string;
  target: string; // CSS selector for the element to highlight
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  action?: 'click' | 'hover' | 'none';
  nextOnClick?: boolean;
}

export const CUSTOMER_TOUR_STEPS: TourStep[] = [
  {
    id: 'sidebar',
    target: '[data-tour="sidebar"]',
    title: 'Navigation Sidebar',
    content: 'Access all features from this sidebar. Click any menu item to navigate. The sidebar can be collapsed using the arrow button.',
    placement: 'right',
  },
  {
    id: 'dashboard',
    target: '[data-tour="dashboard"]',
    title: 'Dashboard',
    content: 'View KPIs, charts, and performance metrics. Click on any chart to drill down into detailed data.',
    placement: 'right',
  },
  {
    id: 'purchasing',
    target: '[data-tour="purchasing"]',
    title: 'Purchasing',
    content: 'Manage Purchase Orders, PO Lines, Forecasts, ASNs, and Deliveries. Use tabs to switch between different views.',
    placement: 'right',
  },
  {
    id: 'quality',
    target: '[data-tour="quality"]',
    title: 'Quality Management',
    content: 'Track NCRs (Non-Conformance Reports), concessions, and quality metrics.',
    placement: 'right',
  },
  {
    id: 'suppliers',
    target: '[data-tour="suppliers"]',
    title: 'Suppliers',
    content: 'View and manage supplier profiles, onboarding status, and send new supplier invitations.',
    placement: 'right',
  },
  {
    id: 'topbar',
    target: '[data-tour="topbar"]',
    title: 'Top Bar',
    content: 'Quick access to search, notifications, view selector, and your profile settings.',
    placement: 'bottom',
  },
  {
    id: 'view-selector',
    target: '[data-tour="view-selector"]',
    title: 'View Selector',
    content: 'Switch between Company, Division, and Group level views to see data at different organizational levels.',
    placement: 'bottom',
  },
  {
    id: 'help',
    target: '[data-tour="help"]',
    title: 'Help & Support',
    content: 'Access documentation, FAQs, and contact support from here.',
    placement: 'right',
  },
];

export const SUPPLIER_TOUR_STEPS: TourStep[] = [
  {
    id: 'sidebar',
    target: '[data-tour="sidebar"]',
    title: 'Navigation Sidebar',
    content: 'Access all features from this sidebar. The sidebar can be collapsed using the arrow button.',
    placement: 'right',
  },
  {
    id: 'dashboard',
    target: '[data-tour="dashboard"]',
    title: 'Your Dashboard',
    content: 'View your performance metrics, open orders, and delivery status at a glance.',
    placement: 'right',
  },
  {
    id: 'my-onboarding',
    target: '[data-tour="my-onboarding"]',
    title: 'Onboarding Progress',
    content: 'Complete your onboarding questionnaires and upload required documents here.',
    placement: 'right',
  },
  {
    id: 'purchasing',
    target: '[data-tour="purchasing"]',
    title: 'Purchase Orders',
    content: 'View and manage your purchase orders, acknowledge deliveries, and create ASNs.',
    placement: 'right',
  },
  {
    id: 'quality',
    target: '[data-tour="quality"]',
    title: 'Quality',
    content: 'Track NCRs raised against your deliveries and respond to quality issues.',
    placement: 'right',
  },
  {
    id: 'topbar',
    target: '[data-tour="topbar"]',
    title: 'Top Bar',
    content: 'View notifications and access your profile settings.',
    placement: 'bottom',
  },
];

interface TourContextType {
  // Tour state
  isTourActive: boolean;
  currentStepIndex: number;
  tourSteps: TourStep[];
  hasSeenTour: boolean;
  showWelcome: boolean;

  // Actions
  startTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  skipTour: () => void;
  dismissWelcome: () => void;
  resetTour: () => void;
  showQuickReference: boolean;
  setShowQuickReference: (show: boolean) => void;

  // Section tour state
  isSectionTourActive: boolean;
  currentSectionTour: SectionTourConfig | null;
  sectionTourStepIndex: number;

  // Section tour actions
  startSectionTour: (pageKey: string, sectionKey: string) => void;
  endSectionTour: () => void;
  nextSectionStep: () => void;
  prevSectionStep: () => void;
  skipSectionTour: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

const TOUR_STORAGE_KEY = 'helix_tour_completed';
const WELCOME_STORAGE_KEY = 'helix_welcome_dismissed';

/**
 * Provides guided tour state for both the main application walkthrough and section-level tours.
 * Tracks completion in localStorage per user and supports customer/supplier tour variants.
 */
export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isImpersonating } = useAuth();
  const { menuVisibility } = useMenuSettings();

  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showQuickReference, setShowQuickReference] = useState(false);

  // Section tour state
  const [isSectionTourActive, setIsSectionTourActive] = useState(false);
  const [currentSectionTour, setCurrentSectionTour] = useState<SectionTourConfig | null>(null);
  const [sectionTourStepIndex, setSectionTourStepIndex] = useState(0);

  // Tour step IDs are intentionally aligned with MenuVisibility keys so the
  // tour can drop steps targeting nav items that are hidden for this tenant
  // (e.g. a compliance-only customer hides purchasing / quality / suppliers).
  // Without this filter, the tour tries to highlight [data-tour="purchasing"]
  // which isn't rendered, and the guided tour silently stalls mid-walkthrough.
  const stepIdToVisibilityKey: Record<string, keyof MenuVisibility | undefined> = {
    purchasing: 'purchasing',
    quality: 'quality',
    suppliers: 'suppliers',
    'my-onboarding': 'myOnboarding' as keyof MenuVisibility,
  };
  const tourSteps = useMemo(() => {
    const base = user?.platform === 'SUPPLIER' ? SUPPLIER_TOUR_STEPS : CUSTOMER_TOUR_STEPS;
    return base.filter(step => {
      const key = stepIdToVisibilityKey[step.id];
      if (!key) return true; // unmapped ids (sidebar, dashboard, topbar) always render
      return menuVisibility[key] !== false;
    });
  }, [user?.platform, menuVisibility]);

  // Check localStorage for tour completion status
  useEffect(() => {
    if (isAuthenticated && user) {
      // Never show welcome during impersonation
      if (isImpersonating) {
        setHasSeenTour(true);
        setShowWelcome(false);
        return;
      }

      const tourKey = `${TOUR_STORAGE_KEY}_${user.id}`;
      const welcomeKey = `${WELCOME_STORAGE_KEY}_${user.id}`;

      const tourCompleted = localStorage.getItem(tourKey) === 'true';
      const welcomeDismissed = localStorage.getItem(welcomeKey) === 'true';

      setHasSeenTour(tourCompleted);

      // Only show welcome once — if the user has logged in before (has any
      // helix_auth data), auto-dismiss. This prevents repeat popups after
      // localStorage is partially cleared during login/impersonation flows.
      if (!welcomeDismissed && !tourCompleted) {
        // First time ever for this user — show welcome, then it gets dismissed
        // Check if any previous session existed (token was stored before)
        const hasLoggedInBefore = localStorage.getItem('helix_remember_me') !== null;
        if (hasLoggedInBefore) {
          // Returning user whose dismiss flag was lost — auto-dismiss
          localStorage.setItem(welcomeKey, 'true');
          setShowWelcome(false);
        } else {
          setShowWelcome(true);
        }
      } else {
        setShowWelcome(false);
      }
    }
  }, [isAuthenticated, user, isImpersonating]);

  const startTour = useCallback(() => {
    setShowWelcome(false);
    setCurrentStepIndex(0);
    setIsTourActive(true);
  }, []);

  const endTour = useCallback(() => {
    setIsTourActive(false);
    setCurrentStepIndex(0);

    if (user) {
      const tourKey = `${TOUR_STORAGE_KEY}_${user.id}`;
      localStorage.setItem(tourKey, 'true');
      setHasSeenTour(true);
    }
  }, [user]);

  const nextStep = useCallback(() => {
    if (currentStepIndex < tourSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      endTour();
    }
  }, [currentStepIndex, tourSteps.length, endTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < tourSteps.length) {
      setCurrentStepIndex(index);
    }
  }, [tourSteps.length]);

  const skipTour = useCallback(() => {
    endTour();
  }, [endTour]);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    if (user) {
      const welcomeKey = `${WELCOME_STORAGE_KEY}_${user.id}`;
      localStorage.setItem(welcomeKey, 'true');
    }
  }, [user]);

  const resetTour = useCallback(() => {
    if (user) {
      const tourKey = `${TOUR_STORAGE_KEY}_${user.id}`;
      const welcomeKey = `${WELCOME_STORAGE_KEY}_${user.id}`;
      localStorage.removeItem(tourKey);
      localStorage.removeItem(welcomeKey);
      setHasSeenTour(false);
      setShowWelcome(true);
    }
  }, [user]);

  // Section tour methods
  const startSectionTour = useCallback((pageKey: string, sectionKey: string) => {
    const tour = getSectionTour(pageKey, sectionKey);
    if (tour) {
      // End any active main tour
      setIsTourActive(false);
      setCurrentStepIndex(0);

      // Start section tour
      setCurrentSectionTour(tour);
      setSectionTourStepIndex(0);
      setIsSectionTourActive(true);
    }
  }, []);

  const endSectionTour = useCallback(() => {
    setIsSectionTourActive(false);
    setSectionTourStepIndex(0);
    setCurrentSectionTour(null);
  }, []);

  const nextSectionStep = useCallback(() => {
    if (currentSectionTour && sectionTourStepIndex < currentSectionTour.steps.length - 1) {
      setSectionTourStepIndex(prev => prev + 1);
    } else {
      endSectionTour();
    }
  }, [currentSectionTour, sectionTourStepIndex, endSectionTour]);

  const prevSectionStep = useCallback(() => {
    if (sectionTourStepIndex > 0) {
      setSectionTourStepIndex(prev => prev - 1);
    }
  }, [sectionTourStepIndex]);

  const skipSectionTour = useCallback(() => {
    endSectionTour();
  }, [endSectionTour]);

  return (
    <TourContext.Provider
      value={{
        isTourActive,
        currentStepIndex,
        tourSteps,
        hasSeenTour,
        showWelcome,
        startTour,
        endTour,
        nextStep,
        prevStep,
        goToStep,
        skipTour,
        dismissWelcome,
        resetTour,
        showQuickReference,
        setShowQuickReference,
        // Section tour
        isSectionTourActive,
        currentSectionTour,
        sectionTourStepIndex,
        startSectionTour,
        endSectionTour,
        nextSectionStep,
        prevSectionStep,
        skipSectionTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

/**
 * Accesses the guided tour state and step navigation controls.
 * Must be used within a {@link TourProvider}.
 *
 * @returns Tour state with step navigation, welcome modal controls, and section tour support
 */
export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
